import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import api from '../../api';
import { DEFAULT_ROWS_PER_PAGE } from '../../utils/paginationUtils';

export const createOrder = createAsyncThunk('orders/create', (args, { rejectWithValue }) => {
  const { user, object_ids, hold_token, payment_type, receipt_nr, language, voucher_code } = args;
  return api
    .post(`/orders`, { user, object_ids, hold_token, payment_type, receipt_nr, language, voucher_code })
    .then((response) => ({ status: response.status, data: response.data }))
    .catch((err) => rejectWithValue({ status: err.response?.status, data: err.response?.data }));
});

export const fetchOrders = createAsyncThunk('orders/list', (args = {}) => {
  const { eventId, search, from, to, status, page, limit } = args;
  const safeStatus = status || 'ALL';

  if (eventId) {
    return api
      .get(
        `/events/${eventId}/orders?page=${encodeURIComponent(page)}&limit=${encodeURIComponent(
          limit
        )}&search=${encodeURIComponent(search)}`
      )
      .then((response) => response.data);
  } else {
    return api
      .get(
        `/orders?to=${encodeURIComponent(to)}&from=${encodeURIComponent(from)}&status=${encodeURIComponent(
          safeStatus
        )}&search=${encodeURIComponent(search)}&page=${encodeURIComponent(page)}&limit=${encodeURIComponent(limit)}`
      )
      .then((response) => response.data);
  }
});

export const updateOrder = createAsyncThunk('orders/update', (args, { dispatch, getState }) => {
  const { order_id, status, sendEmail } = args;
  const state = getState();
  const options = {
    ...state.orders.options,
    to: dayjs(state.orders.options.to),
    from: dayjs(state.orders.options.from),
  };
  return api.patch(`/orders/${order_id}`, { status, sendEmail }).then(() => dispatch(fetchOrders(options)).unwrap());
});

export const refundTickets = createAsyncThunk(
  'orders/refundTickets',
  (args, { dispatch, getState, rejectWithValue }) => {
    const { ticket_ids, sendEmail } = args;
    const state = getState();
    const options = {
      ...state.orders.options,
      to: dayjs(state.orders.options.to),
      from: dayjs(state.orders.options.from),
    };

    return api
      .post('/refunds/tickets', { ticket_ids, sendEmail })
      .then((response) => {
        dispatch(fetchOrders(options));
        return response.data;
      })
      .catch((err) => rejectWithValue({ status: err.response?.status, data: err.response?.data }));
  }
);

const initialState = {
  data: [],
  totalItems: 0,
  error: null,
  isLoading: false,
  isUpdating: false,

  options: {
    search: '',
    from: null,
    to: null,
    status: 'ALL',
    page: 1,
    limit: DEFAULT_ROWS_PER_PAGE,
  },
};

const ordersSlice = createSlice({
  name: 'orders',
  initialState: initialState,
  reducers: {
    setFilters: (state, action) => {
      const { search, from, to, status, page, limit } = action.payload;

      if (search !== undefined) state.options.search = search;
      if (from !== undefined) state.options.from = from;
      if (to !== undefined) state.options.to = to;
      if (status !== undefined) state.options.status = status;
      if (page !== undefined) state.options.page = page;
      if (limit !== undefined) state.options.limit = limit;
    },
    clearOrders: (state) => {
      state.data = [];
      state.options = {
        search: '',
        from: null,
        to: null,
        status: 'ALL',
        page: 1,
        limit: DEFAULT_ROWS_PER_PAGE,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(createOrder.pending, (state, action) => {
      state.isLoading = true;
      state.error = null;
    });

    builder.addCase(createOrder.fulfilled, (state, action) => {
      state.isLoading = false;
    });

    builder.addCase(createOrder.rejected, (state, action) => {
      state.isLoading = false;
    });

    builder.addCase(fetchOrders.pending, (state, action) => {
      state.isLoading = true;
      state.error = null;
    });

    builder.addCase(fetchOrders.fulfilled, (state, action) => {
      state.isLoading = false;
      const orders = action.payload.data;
      state.data = orders.map((o) => ({
        ...o,
        tickets: Array.isArray(o.tickets) ? o.tickets : [],
      }));
      state.totalItems = action.payload.totalItems;
      state.options.page = action.payload.currentPage;
    });

    builder.addCase(fetchOrders.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message;
    });

    builder.addCase(updateOrder.pending, (state, action) => {
      state.isUpdating = true;
      state.error = null;
    });

    builder.addCase(updateOrder.fulfilled, (state, action) => {
      state.isUpdating = false;
    });

    builder.addCase(updateOrder.rejected, (state, action) => {
      state.isUpdating = false;
      state.error = action.error.message;
    });

    builder.addCase(refundTickets.pending, (state, action) => {
      state.isUpdating = true;
      state.error = null;
    });

    builder.addCase(refundTickets.fulfilled, (state, action) => {
      state.isUpdating = false;
    });

    builder.addCase(refundTickets.rejected, (state, action) => {
      state.isUpdating = false;
      state.error = action.error.message;
    });
  },
});

export const { setFilters: setOrdersFilter, clearOrders } = ordersSlice.actions;
export default ordersSlice.reducer;
